import { motion } from 'motion/react';
import { Sparkles, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AppDrawer } from '@/components/AppDrawer';
import { Bar, IconDisc, pct } from '@/components/kit';
import { Pet } from '@/components/Pet';
import { m, msg } from '@/lib/i18n';
import type { AppState } from '@/lib/types';

/** The weekly recap: what the household got done in the last 7 days. */
export function RecapDrawer({ state, open, onOpenChange }: { state: AppState; open: boolean; onOpenChange: (o: boolean) => void }) {
  const r = state.recap;
  const diff = r.total - r.previousTotal;
  const solo = state.members.length === 1;
  // Like Headway's weekly recap: each number carries its change since last week.
  const stats: { label: string; value: number; delta?: number }[] = solo
    ? [{ label: m.recap_your_chores(), value: r.mine, delta: diff }, { label: 'XP', value: r.xp }]
    : [
        { label: m.recap_your_chores(), value: r.mine },
        { label: m.recap_household_chores(), value: r.total, delta: diff },
        { label: 'XP', value: r.xp },
      ];
  return (
    <AppDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={m.recap_title()}
      description={m.recap_subtitle()}
      footer={
        <Button size="lg" onClick={() => onOpenChange(false)}>
          {m.continue()}
        </Button>
      }
    >
      <div className={`grid gap-2 ${solo ? 'grid-cols-2' : 'grid-cols-3'}`}>
        {stats.map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }} className="bg-card grid justify-items-center gap-1 rounded-2xl border p-4 text-center">
            {s.delta !== undefined && s.delta !== 0 ? (
              <span className={`rounded-full px-1.5 text-xs font-semibold ${s.delta > 0 ? 'bg-accent text-accent-foreground' : 'bg-muted text-muted-foreground'}`}>
                {s.delta > 0 ? `+${s.delta}` : s.delta}
              </span>
            ) : (
              <span className="h-4" />
            )}
            <div className="text-3xl font-bold tabular-nums">{s.value}</div>
            <div className="text-muted-foreground text-xs">{s.label}</div>
          </motion.div>
        ))}
      </div>
      <div className="bg-card divide-border divide-y rounded-2xl border">
        {!solo && r.top && (
          <div className="flex items-center gap-3 p-4">
            <IconDisc tone="accent">
              <Trophy />
            </IconDisc>
            <div className="text-[15px]">{m.recap_top({ name: r.top.name, count: r.top.count })}</div>
          </div>
        )}
        <div className="grid gap-2 p-4">
          <div className="flex items-center justify-between text-[15px]">
            <span className="flex items-center gap-2">
              <Sparkles className="text-muted-foreground size-4" /> {m.cleanliness()}
            </span>
            <strong>{pct(state.health)}</strong>
          </div>
          <Bar value={state.health} tone="health" />
        </div>
      </div>
      <div className="flex items-center justify-center gap-3">
        <Pet kind={state.pet.kind} outfit={state.pet.outfit} mood={state.pet.mood.id} size={84} scale={state.pet.size} crown={state.pet.crown} />
        <p className="text-muted-foreground text-sm">
          {state.pet.name} · {msg(`pet_${state.pet.kind}_stage_${state.pet.stage}`)}
        </p>
      </div>
    </AppDrawer>
  );
}
